import { isValid as isValidDate, parse as parseDate } from 'date-fns';

import { toDateString } from 'utils/date';

import type {
  ExcelColumnSpec,
  ExcelColumnSpecResolver,
  ExcelFinding,
  ExcelParseLabels,
  ExcelRow
} from './ExcelImport.types';

// Minimal shape of what we read off an ExcelJS worksheet - narrow on purpose so this
// stays testable without pulling the real (lazily-loaded) library into unit tests.
export interface ExcelWorksheetLike {
  getRow: (rowNumber: number) => {
    actualCellCount: number;
    getCell: (columnNumber: number) => { value: unknown };
  };
  eachRow: (
    options: { includeEmpty: boolean },
    callback: (
      row: { getCell: (columnNumber: number) => { value: unknown } },
      rowNumber: number
    ) => void
  ) => void;
}

const NUMBER_PATTERN = /^-?\d+([.,]\d+)?$/;
const DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

// ExcelJS hands back an object for anything that isn't a plain value: a formula cell is
// { formula, result }, a cell with mixed formatting { richText }, a link { text, hyperlink }
// and a broken formula { error }. Without unwrapping, String(value) is "[object Object]" -
// stored verbatim in text columns and reported as a bogus finding in numeric ones.
const toPrimitive = (value: unknown): unknown => {
  if (!value || typeof value !== 'object' || value instanceof Date)
    return value;
  const cell = value as {
    error?: unknown;
    richText?: { text: string }[];
    result?: unknown;
    text?: unknown;
  };
  // Keep the error text so it lands in a finding instead of passing as an empty cell.
  if (cell.error !== undefined) return String(cell.error);
  if (cell.richText) return cell.richText.map((part) => part.text).join('');
  if (cell.result !== undefined) return toPrimitive(cell.result);
  if (cell.text !== undefined) return cell.text;

  // An object shape we can't read is not a value (e.g. a formula cell without a
  // cached result): treat it as empty rather than letting String() turn it into
  // "[object Object]" in a text column.
  return undefined;
};

const isEmptyCell = (value: unknown) => {
  const primitive = toPrimitive(value);
  return (
    primitive === null ||
    primitive === undefined ||
    String(primitive).trim() === ''
  );
};

export const normalizeHeader = (value: unknown): string =>
  String(value ?? '')
    .trim()
    .toLowerCase()
    .replace(/[,:]+$/, '');

export interface CoercedCell {
  value?: number | string;
  isEmpty?: boolean;
  isValid: boolean;
}

export const coerceNumberCell = (rawValue: unknown): CoercedCell => {
  const value = toPrimitive(rawValue);
  if (isEmptyCell(value)) return { isEmpty: true, isValid: true };
  if (typeof value === 'number') {
    return { value, isValid: Number.isFinite(value) };
  }
  const trimmed = String(value).trim();
  if (!NUMBER_PATTERN.test(trimmed)) return { isValid: false };
  return { value: Number(trimmed.replace(',', '.')), isValid: true };
};

export const coerceTextCell = (rawValue: unknown): CoercedCell => {
  const value = toPrimitive(rawValue);
  if (isEmptyCell(value)) return { isEmpty: true, isValid: true };
  return { value: String(value).trim(), isValid: true };
};

export const coerceDateCell = (rawValue: unknown): CoercedCell => {
  const value = toPrimitive(rawValue);
  if (isEmptyCell(value)) return { isEmpty: true, isValid: true };
  if (value instanceof Date) {
    return {
      value: toDateString(value),
      isValid: !Number.isNaN(value.getTime())
    };
  }
  const trimmed = String(value).trim();
  if (
    DATE_PATTERN.test(trimmed) &&
    isValidDate(parseDate(trimmed, 'yyyy-MM-dd', new Date()))
  ) {
    return { value: trimmed, isValid: true };
  }
  return { isValid: false };
};

export const resolveColumns = (
  resolver: ExcelColumnSpecResolver,
  columnCount: number
): ExcelColumnSpec[] | null =>
  typeof resolver === 'function' ? resolver(columnCount) : resolver;

export interface ParsedWorksheet {
  rows: ExcelRow[];
  findings: ExcelFinding[];
  tooManyRows: { found: number; max: number } | null;
}

export const parseWorksheetRows = (
  worksheet: ExcelWorksheetLike,
  columns: ExcelColumnSpec[],
  maxRows: number,
  labels: ExcelParseLabels
): ParsedWorksheet => {
  const findings: ExcelFinding[] = [];

  const headerRow = worksheet.getRow(1);
  columns.forEach((spec, index) => {
    const headerValue = toPrimitive(headerRow.getCell(index + 1).value);
    if (normalizeHeader(headerValue) !== normalizeHeader(spec.header)) {
      findings.push({
        rowNumber: 1,
        column: String(index + 1),
        expected: spec.header,
        found: String(headerValue ?? '')
      });
    }
  });

  const rows: ExcelRow[] = [];
  let dataRowCount = 0;

  worksheet.eachRow({ includeEmpty: false }, (row, rowNumber) => {
    if (rowNumber === 1) return;
    dataRowCount += 1;
    if (dataRowCount > maxRows) return;

    const parsedRow: ExcelRow = { rowNumber };

    columns.forEach((spec, index) => {
      if (spec.type === 'skip' || !spec.key) return;
      const cellValue = row.getCell(index + 1).value;
      const coerced =
        spec.type === 'number'
          ? coerceNumberCell(cellValue)
          : spec.type === 'text'
          ? coerceTextCell(cellValue)
          : coerceDateCell(cellValue);

      const isMissingRequired = Boolean(spec.required && coerced.isEmpty);

      if (!coerced.isValid || isMissingRequired) {
        findings.push({
          rowNumber,
          column: spec.header,
          expected: isMissingRequired
            ? labels.expectedRequired
            : spec.type === 'number'
            ? labels.expectedNumber
            : labels.expectedDate,
          found: String(toPrimitive(cellValue) ?? '')
        });
      } else if (!coerced.isEmpty) {
        parsedRow[spec.key as string] = coerced.value as number | string;
      }
    });

    rows.push(parsedRow);
  });

  return {
    rows,
    findings,
    tooManyRows:
      dataRowCount > maxRows ? { found: dataRowCount, max: maxRows } : null
  };
};

export const readExcelFile = async (
  file: File,
  columnsResolver: ExcelColumnSpecResolver,
  maxRows: number,
  labels: ExcelParseLabels
): Promise<ParsedWorksheet> => {
  const { Workbook } = await import('exceljs');
  const workbook = new Workbook();
  const buffer = await file.arrayBuffer();
  await workbook.xlsx.load(buffer);

  const worksheet = workbook.worksheets[0] as unknown as ExcelWorksheetLike;
  const columnCount = worksheet.getRow(1).actualCellCount;
  const columns = resolveColumns(columnsResolver, columnCount);

  if (!columns) {
    return {
      rows: [],
      findings: [
        {
          rowNumber: 1,
          expected: labels.unsupportedColumnCountExpected,
          found: String(columnCount)
        }
      ],
      tooManyRows: null
    };
  }

  return parseWorksheetRows(worksheet, columns, maxRows, labels);
};
